import { createHandleKeyDown } from '@/features/ClusterConfigs/utils/handleKeyDown'
import {
  Button,
  Field,
  Icon,
  Popover,
  Textarea,
  Tooltip,
} from '@chakra-ui/react'
import { Check, ChevronDown } from 'lucide-react'
import { useRef } from 'react'

export type SubmitWithCommentPopoverProps = Readonly<{
  onSubmit: () => void
  isReadyToSubmit: boolean
  comment: string
  commentError?: string
  onCommentChange: (comment: string) => void
  translations: {
    buttonText: string | null
    popoverTitle: string | null
    descriptionLabel?: string | null
    commentPlaceholder: string | null
    submitButtonText: string | null
    invalidConfigTooltip: string | null
  }
}>

export function SubmitWithCommentPopover({
  onSubmit,
  isReadyToSubmit,
  onCommentChange,
  comment,
  commentError,
  translations,
}: SubmitWithCommentPopoverProps) {
  const popoverTextareaRef = useRef(null)
  const submitButtonRef = useRef<HTMLButtonElement>(null)

  const onKeyDown = createHandleKeyDown(submitButtonRef)

  return (
    <Popover.Root
      positioning={{ placement: 'bottom-end' }}
      initialFocusEl={() => popoverTextareaRef.current}
    >
      <Popover.Trigger asChild>
        <Button variant="solid">
          {translations.buttonText}
          <Icon as={ChevronDown} mr={-1} />
        </Button>
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content>
          <Popover.Header fontWeight="semibold" border={0}>
            {translations.popoverTitle}
            <Popover.CloseTrigger top={2} />
          </Popover.Header>
          <Popover.Body>
            <Field.Root
              display="flex"
              alignItems="start"
              invalid={!!commentError}
            >
              {translations.descriptionLabel && (
                <Field.Label>{translations.descriptionLabel}</Field.Label>
              )}

              <Textarea
                placeholder={translations.commentPlaceholder || ''}
                value={comment}
                onChange={(e) => onCommentChange(e.target.value)}
                borderColor={commentError ? 'content.error' : 'border'}
                onKeyDown={onKeyDown}
                ref={popoverTextareaRef}
              />
              {commentError && (
                <Field.ErrorText position="absolute" bottom="-1.2rem">
                  {commentError}
                </Field.ErrorText>
              )}
            </Field.Root>
          </Popover.Body>
          <Popover.Footer display="flex" justifyContent="end" border={0}>
            <Tooltip.Root disabled={isReadyToSubmit}>
              <Tooltip.Trigger asChild>
                <Button
                  variant="solid"
                  onClick={onSubmit}
                  ref={submitButtonRef}
                >
                  <Icon as={Check} />
                  {translations.submitButtonText}
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Positioner>
                <Tooltip.Content>
                  {translations.invalidConfigTooltip}
                </Tooltip.Content>
              </Tooltip.Positioner>
            </Tooltip.Root>
          </Popover.Footer>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  )
}
